/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Dropdown,
  Input,
  Modal,
  Table,
  Tag,
  message,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { rolesApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const { Search } = Input;

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const RolesList = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    role: null,
  });
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions using the standardized hasPermission utility
  const canViewRoles = useMemo(() => {
    return hasPermission(userPermissions, "roles.view");
  }, [userPermissions]);

  const canCreateRoles = useMemo(() => {
    return hasPermission(userPermissions, "roles.create");
  }, [userPermissions]);

  const canEditRoles = useMemo(() => {
    return hasPermission(userPermissions, "roles.edit");
  }, [userPermissions]);

  const canDeleteRoles = useMemo(() => {
    return hasPermission(userPermissions, "roles.delete");
  }, [userPermissions]);

  useEffect(() => {
    if (canViewRoles) {
      fetchRoles();
    }
    setIsCheckingPermission(false);
  }, [canViewRoles]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await rolesApi.getAll();
      setRoles(response || []);
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to view roles");
      } else {
        message.error(error?.response?.data?.message || "Failed to load roles");
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return roles;

    return roles.filter((role) => {
      const haystack = [
        role.name,
        role.description,
        ...(role.permissions || []).map((permission) => permission.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [roles, search]);

  const handleDelete = async () => {
    if (!canDeleteRoles) {
      message.error("You don't have permission to delete roles");
      setDeleteModal({ visible: false, role: null });
      return;
    }

    if (!deleteModal.role) return;

    try {
      setDeleting(true);
      await rolesApi.delete(deleteModal.role.id);
      message.success(`Role ${deleteModal.role.name} deleted successfully`);
      setDeleteModal({ visible: false, role: null });
      setRoles((current) =>
        current.filter((role) => role.id !== deleteModal.role.id),
      );
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to delete this role");
      } else {
        const msg = error?.response?.data?.message;
        if (Array.isArray(msg)) msg.forEach((item) => message.error(item));
        else message.error(msg || "Failed to delete role");
      }
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: "Role",
      key: "role",
      render: (_, record) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong style={{ color: "#1a1d2e" }}>{record.name}</strong>
            {record.isSystem && <Tag color="gold">System</Tag>}
          </div>
          <div className="small text-muted" style={{ marginTop: 4 }}>
            {record.description || "No description provided"}
          </div>
        </div>
      ),
    },
    {
      title: "Permissions",
      key: "permissions",
      render: (_, record) => {
        const permissions = record.permissions || [];
        const preview = permissions.slice(0, 3);
        const remaining = permissions.length - preview.length;

        return (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Tag color="blue">
                <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                {permissions.length} permissions
              </Tag>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {preview.map((permission) => (
                <Tag key={permission.id}>{permission.name}</Tag>
              ))}
              {remaining > 0 && <Tag>+{remaining} more</Tag>}
            </div>
          </div>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => {
        const items = [];

        if (canEditRoles) {
          items.push({
            key: "edit",
            label: "Edit Role",
            icon: <EditOutlined />,
            onClick: () => history.push(`/roles/${record.id}/edit`),
          });
        }

        if (canDeleteRoles && !record.isSystem) {
          if (items.length > 0) {
            items.push({ type: "divider" });
          }
          items.push({
            key: "delete",
            label: "Delete Role",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => setDeleteModal({ visible: true, role: record }),
          });
        }

        if (items.length === 0) {
          return null;
        }

        return (
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission check for viewing the page
  if (!canViewRoles) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view roles.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> roles.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Roles & Permissions</h5>
                  {canCreateRoles && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/roles/add")}
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                    >
                      Add Role
                    </Button>
                  )}
                </div>
              </div>

              <Alert
                className="mb-4"
                showIcon
                type="info"
                message="Dynamic access control"
                description="Create custom dashboard roles, choose the exact permissions they get, and assign those roles to users from the backend."
              />

              {/* Show view-only mode message if user has view but not create/edit/delete permissions */}
              {!canCreateRoles && !canEditRoles && !canDeleteRoles && (
                <Alert
                  message="View Only Mode"
                  description="You have view-only access to roles. To create, edit, or delete roles, please contact an administrator."
                  type="warning"
                  showIcon
                  className="mb-4"
                />
              )}

              <div className="row mb-4">
                <div className="col-md-5">
                  <Search
                    placeholder="Search role name, description, or permission"
                    allowClear
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onSearch={(value) => setSearch(value)}
                  />
                </div>
              </div>

              <ResponsiveTable
                columns={columns}
                dataSource={filteredRoles}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: true }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#dc2626" }} />
            Delete Role
          </span>
        }
        open={deleteModal.visible}
        onOk={handleDelete}
        onCancel={() => setDeleteModal({ visible: false, role: null })}
        confirmLoading={deleting}
        okText="Delete Role"
        okType="danger"
      >
        <Alert
          message="This role will be removed from the system."
          description="Users assigned to this role must be reassigned before deletion."
          type="warning"
          showIcon
          className="mb-3"
        />
        <p>
          Delete role <strong>{deleteModal.role?.name}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default RolesList;
